import type { AST } from '@eslint-community/regexpp';

export type RegexGraphNodeKind =
  | 'start'
  | 'end'
  | 'token'
  | 'group'
  | 'assertion'
  | 'junction'
  | 'unknown';

export interface RegexGraphNode {
  id: string;
  kind: RegexGraphNodeKind;
  label: string;
  range?: { start: number; end: number };
}

export interface RegexGraphEdge {
  id: string;
  from: string;
  to: string;
}

export interface RegexGraphGroup {
  id: string;
  label: string;
  nodeIds: string[];
  repeat?: string;
}

export interface RegexGraph {
  nodes: RegexGraphNode[];
  edges: RegexGraphEdge[];
  groups: RegexGraphGroup[];
}

export interface RegexGraphOptions {
  maxNodes?: number;
}

type Fragment = { entries: string[]; exits: string[]; ownGroupId?: string };

function formatQuantifier(min: number, max: number, greedy: boolean): string {
  const suffix = greedy ? '' : '?';

  if (min === 0 && max === 1) return `?${suffix}`;
  if (min === 0 && max === Infinity) return `*${suffix}`;
  if (min === 1 && max === Infinity) return `+${suffix}`;

  if (max === Infinity) return `{${min},}${suffix}`;
  if (min === max) return `{${min}}${suffix}`;
  return `{${min},${max}}${suffix}`;
}

function quantifierDescription(min: number, max: number): string {
  if (min === 0 && max === 1) return '0 or 1 time';
  if (min === 0 && max === Infinity) return '0 or more times';
  if (min === 1 && max === Infinity) return '1 or more times';
  if (min === max) return `${min} times`;
  if (max === Infinity) return `${min} or more times`;
  return `${min} to ${max} times`;
}

function assertionLabel(node: AST.LookaroundAssertion): string {
  if (node.kind === 'lookbehind') return node.negate ? 'Negative lookbehind' : 'Lookbehind';
  return node.negate ? 'Negative lookahead' : 'Lookahead';
}

function isLookaroundAssertion(node: AST.Assertion): node is AST.LookaroundAssertion {
  return (node as AST.LookaroundAssertion).alternatives !== undefined;
}

function elementLabel(node: AST.Node): string {
  // Prefer raw for correctness (escapes, unicode, etc.)
  return node.raw || node.type;
}

function assertionTokenLabel(node: AST.Assertion): string {
  if (isLookaroundAssertion(node)) {
    return assertionLabel(node);
  }

  // Edge assertions: ^ and $
  if ((node as AST.EdgeAssertion).kind === 'start') return 'Starts with (^)';
  if ((node as AST.EdgeAssertion).kind === 'end') return 'Ends with ($)';

  // Word boundary: \b / \B
  if ((node as AST.WordBoundaryAssertion).kind === 'word') {
    const wb = node as AST.WordBoundaryAssertion;
    return wb.negate ? 'Not word boundary (\\B)' : 'Word boundary (\\b)';
  }

  // Fallback
  return elementLabel(node);
}

function characterSetLabel(node: AST.CharacterSet): string {
  if (node.type !== 'CharacterSet') return elementLabel(node);

  if ((node as AST.AnyCharacterSet).kind === 'any') return 'Any character (.)';

  if ((node as AST.EscapeCharacterSet).kind === 'digit') {
    const s = node as AST.EscapeCharacterSet;
    return s.negate ? 'Not digit (\\D)' : 'Digit (\\d)';
  }
  if ((node as AST.EscapeCharacterSet).kind === 'space') {
    const s = node as AST.EscapeCharacterSet;
    return s.negate ? 'Not whitespace (\\S)' : 'Whitespace (\\s)';
  }
  if ((node as AST.EscapeCharacterSet).kind === 'word') {
    const s = node as AST.EscapeCharacterSet;
    return s.negate ? 'Not word char (\\W)' : 'Word char (\\w)';
  }

  return elementLabel(node);
}

function charClassElementLabel(el: AST.CharacterClassElement): string {
  // Prefer raw for fidelity.
  // For simple Characters, raw is typically the literal character.
  return (el as unknown as { raw?: string }).raw || (el as unknown as { type: string }).type;
}

function characterClassLabel(node: AST.CharacterClass): string {
  const header = node.negate ? 'None of:' : 'Any of:';

  // Common simple case: a single literal char like [^=] or [&]
  if (node.elements.length === 1) {
    const only = node.elements[0];
    const body = charClassElementLabel(only);
    return `${header}\n${body}`;
  }

  // Keep it readable for larger classes but still avoid losing meaning.
  const body = node.elements.map(charClassElementLabel).filter(Boolean).join('');
  if (body) return `${header}\n${body}`;
  return node.raw || `${header}\n[...]`;
}

function parseTrailingQuantifier(label: string): { baseLabel: string; min: number; max: number; greedy: boolean } | null {
  // Handles suffixes produced by formatQuantifier(): ?, ??, *, *?, +, +?, {n}, {n}?, {n,}, {n,}?, {n,m}, {n,m}?
  const s = label;

  // Curly quantifiers
  const braceMatch = s.match(/\{(\d+)(?:,(\d*)?)?\}(\?)?$/);
  if (braceMatch) {
    const min = Number(braceMatch[1]);
    const greedy = braceMatch[3] !== '?';
    const hasComma = braceMatch[0].includes(',');
    const maxRaw = braceMatch[2];
    const max = !hasComma ? min : maxRaw === undefined || maxRaw === '' ? Infinity : Number(maxRaw);

    if (!Number.isFinite(min) || min < 0) return null;
    if (max !== Infinity && (!Number.isFinite(max) || max < 0)) return null;
    if (max !== Infinity && min > max) return null;

    const baseLabel = s.slice(0, s.length - braceMatch[0].length);
    return { baseLabel, min, max, greedy };
  }

  // Fixed-symbol quantifiers
  const twoChar = s.slice(-2);
  if (twoChar === '??') return { baseLabel: s.slice(0, -2), min: 0, max: 1, greedy: false };
  if (twoChar === '*?') return { baseLabel: s.slice(0, -2), min: 0, max: Infinity, greedy: false };
  if (twoChar === '+?') return { baseLabel: s.slice(0, -2), min: 1, max: Infinity, greedy: false };

  const last = s.slice(-1);
  if (last === '?') return { baseLabel: s.slice(0, -1), min: 0, max: 1, greedy: true };
  if (last === '*') return { baseLabel: s.slice(0, -1), min: 0, max: Infinity, greedy: true };
  if (last === '+') return { baseLabel: s.slice(0, -1), min: 1, max: Infinity, greedy: true };

  return null;
}

export function regexAstToGraph(ast: AST.RegExpLiteral, options: RegexGraphOptions = {}): RegexGraph {
  const maxNodes = typeof options.maxNodes === 'number' ? options.maxNodes : 600;

  const nodes: RegexGraphNode[] = [];
  const edges: RegexGraphEdge[] = [];
  const groups: RegexGraphGroup[] = [];
  const nodeIndexById = new Map<string, number>();

  let groupCounter = 0;

  let syntheticId = 0;
  const nextSynthetic = (prefix: string) => `s:${prefix}:${++syntheticId}`;

  const ensureCapacity = () => {
    if (nodes.length >= maxNodes) {
      throw new Error(`Regex diagram exceeded maxNodes (${maxNodes}). Try a smaller regex or increase Max Nodes.`);
    }
  };

  const addNode = (kind: RegexGraphNodeKind, label: string, range?: { start: number; end: number }, id?: string): string => {
    ensureCapacity();
    const nodeId = id ?? nextSynthetic(kind);
    nodeIndexById.set(nodeId, nodes.length);
    nodes.push({ id: nodeId, kind, label, range });
    return nodeId;
  };

  const addEdge = (from: string, to: string) => {
    edges.push({ id: `${from}->${to}`, from, to });
  };

  const appendToTokenLabels = (ids: string[], suffix: string) => {
    for (const id of ids) {
      const idx = nodeIndexById.get(id);
      if (idx === undefined) continue;
      const node = nodes[idx];
      if (node.kind !== 'token') continue;
      // Avoid double-appending if the mapper hits the same node twice.
      if (node.label.endsWith(suffix)) continue;
      node.label = `${node.label}${suffix}`;
    }
  };

  const buildAlternation = (alternatives: AST.Alternative[]): Fragment => {
    if (alternatives.length === 0) {
      const epsilon = addNode('token', 'ε');
      return { entries: [epsilon], exits: [epsilon] };
    }

    if (alternatives.length === 1) return buildAlternative(alternatives[0]);

    const fragments = alternatives.map((a) => buildAlternative(a));
    return {
      entries: fragments.flatMap((f) => f.entries),
      exits: fragments.flatMap((f) => f.exits),
    };
  };

  const buildAlternative = (alt: AST.Alternative): Fragment => {
    if (alt.elements.length === 0) {
      const epsilon = addNode('token', 'ε', { start: alt.start, end: alt.end }, `n:epsilon:${alt.start}-${alt.end}`);
      return { entries: [epsilon], exits: [epsilon] };
    }

    let current = buildElement(alt.elements[0]);

    for (let i = 1; i < alt.elements.length; i++) {
      const next = buildElement(alt.elements[i]);
      for (const from of current.exits) {
        for (const to of next.entries) {
          addEdge(from, to);
        }
      }
      current = { entries: current.entries, exits: next.exits };
    }

    return current;
  };

  const buildElement = (el: AST.Element): Fragment => {
    if (el.type === 'Quantifier') {
      const inner = buildElement(el.element);
      const q = formatQuantifier(el.min, el.max, el.greedy);
      // If the quantified element is a group wrapper we created, annotate the wrapper instead.
      if (inner.ownGroupId) {
        const idx = groups.findIndex((g) => g.id === inner.ownGroupId);
        if (idx >= 0) {
          groups[idx] = { ...groups[idx], repeat: quantifierDescription(el.min, el.max) };
          return inner;
        }
      }

      // Otherwise keep the graph "token-only" by annotating the token exits.
      appendToTokenLabels(inner.exits, q);
      return inner;
    }

    if (el.type === 'Group' || el.type === 'CapturingGroup') {
      const before = nodes.length;
      const frag = buildAlternation(el.alternatives);
      const after = nodes.length;

      const nodeIds = nodes
        .slice(before, after)
        .filter((n) => n.kind !== 'start' && n.kind !== 'end')
        .map((n) => n.id);

      const uniqueNodeIds = Array.from(new Set(nodeIds));
      const isCapturing = el.type === 'CapturingGroup';
      // Always show capturing groups (Regulex-style), but keep non-capturing wrappers only for multi-node groups.
      const shouldWrap = isCapturing ? uniqueNodeIds.length >= 1 : uniqueNodeIds.length >= 2;
      if (!shouldWrap) return frag;

      groupCounter += 1;
      const groupId = `g:${el.start}-${el.end}`;
      groups.push({ id: groupId, label: `Group #${groupCounter}`, nodeIds: uniqueNodeIds });
      return { ...frag, ownGroupId: groupId };
    }

    if (el.type === 'Assertion') {
      const label = assertionTokenLabel(el);
      const nodeId = addNode('token', label, { start: el.start, end: el.end }, `n:assert:${el.start}-${el.end}`);
      return { entries: [nodeId], exits: [nodeId] };
    }

    // Leaf-ish elements
    const id = `n:${el.type}:${el.start}-${el.end}`;

    const label =
      el.type === 'CharacterSet'
        ? characterSetLabel(el)
        : el.type === 'CharacterClass'
          ? characterClassLabel(el)
          : elementLabel(el);
    const nodeId = addNode('token', label, { start: el.start, end: el.end }, id);
    return { entries: [nodeId], exits: [nodeId] };
  };

  const start = addNode('start', 'Start', undefined, 'start');
  const end = addNode('end', 'End', undefined, 'end');

  const patternFrag = buildAlternation(ast.pattern.alternatives);
  for (const entry of patternFrag.entries) {
    addEdge(start, entry);
  }
  for (const exit of patternFrag.exits) {
    addEdge(exit, end);
  }

  // Post-process: if a capturing group contains exactly one token node and the quantifier
  // was attached to that token (e.g. /([^=]+)/), shift the repetition label to the group wrapper
  // to better match common railroad visualizers like Regulex.
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i];
    if (g.repeat) continue;
    if (g.nodeIds.length !== 1) continue;

    const nodeId = g.nodeIds[0];
    const nodeIdx = nodeIndexById.get(nodeId);
    if (nodeIdx === undefined) continue;
    const node = nodes[nodeIdx];
    if (node.kind !== 'token') continue;

    const q = parseTrailingQuantifier(node.label);
    if (!q) continue;

    groups[i] = {
      ...g,
      repeat: `${quantifierDescription(q.min, q.max)}${q.greedy ? '' : ' (lazy)'}`,
    };
    nodes[nodeIdx] = { ...node, label: q.baseLabel };
  }

  return { nodes, edges, groups };
}
